import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers'
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers'
import { useAppActions } from '../../../stores/application/useAppActions'
import { AppRoutes } from '../../../interfaces/store/appStore'
import { isChromeBasedBrowser } from '../../../utils'
import OnboardingStepLayout from '../components/OnboardingStepLayout'
import { ONBOARDING_SKIP_STEPPER_STEPS_BROWSER } from '../components/OnboardingStepper'
import { getBrowserDisplayName } from '../onboardingBrowserUtils'
import { clearSkippedToAllSet } from '../onboardingExtensionSkipUtils'
import {
    AuthOnboardingCard,
    AuthOnboardingCardBody,
    AuthOnboardingIntro,
    AuthMethodButton,
    AuthSkipLink,
    AuthOnboardingActions,
} from '../../authentication/authFormCommon'

const PAGE_TITLE = 'Use Google Chrome to sync LinkedIn contacts'

const PAGE_SUBTITLE =
    'LeadDelta uses a Chrome extension to connect and sync your LinkedIn contacts. Open LeadDelta in Google Chrome for the full experience.'

const SKIP_WARNING =
    'You can still explore LeadDelta, but your LinkedIn contacts will not sync until you open LeadDelta in Google Chrome and install the extension.'

const UnsupportedBrowserSection: FunctionComponent = () => {
    const { saveOnboardingData } = useAuthHandlers()
    const { visitChromeBrowserDownload } = useExternalHandlers()
    const { setOnboardingStep } = useAppActions()
    const navigate = useNavigate()
    const [step, setStep] = useState(1)
    const [loading, setLoading] = useState(false)

    const browserName = getBrowserDisplayName()

    useEffect(() => {
        if (isChromeBasedBrowser()) {
            navigate(AppRoutes.ONBOARDING_EXTENSION_INSTALL, { replace: true })
        }
    }, [navigate])

    const skipToSurveyClick = useCallback(async () => {
        setLoading(true)
        try {
            clearSkippedToAllSet()
            setOnboardingStep(4)
            await saveOnboardingData({ startedExtensionInstallation: false })
            setOnboardingStep(4)
            navigate(AppRoutes.ONBOARDING_SURVEY)
        } finally {
            setLoading(false)
        }
    }, [saveOnboardingData, setOnboardingStep, navigate])

    if (step === 1) {
        return (
            <OnboardingStepLayout
                activeStepIndex={0}
                steps={ONBOARDING_SKIP_STEPPER_STEPS_BROWSER}
                title={PAGE_TITLE}
                subtitle={PAGE_SUBTITLE}
            >
                <AuthOnboardingCard>
                    <AuthOnboardingCardBody>
                        <AuthOnboardingIntro>
                            {`You’re currently using ${browserName}. Open Google Chrome to sync your LinkedIn contacts.`}
                        </AuthOnboardingIntro>

                        <AuthOnboardingActions>
                            <AuthMethodButton
                                $type="blue"
                                $SVGtype="openNewTab"
                                onClickHandler={() => visitChromeBrowserDownload()}
                            >
                                Get Google Chrome
                            </AuthMethodButton>
                            <AuthSkipLink type="button" onClick={() => setStep(2)}>
                                Continue
                            </AuthSkipLink>
                        </AuthOnboardingActions>
                    </AuthOnboardingCardBody>
                </AuthOnboardingCard>
            </OnboardingStepLayout>
        )
    }

    return (
        <OnboardingStepLayout
            activeStepIndex={0}
            steps={ONBOARDING_SKIP_STEPPER_STEPS_BROWSER}
            title={`Are you sure you want to continue in ${browserName}?`}
        >
            <ConfirmStepContent>
                <AuthOnboardingCard>
                    <AuthOnboardingCardBody>
                        <AuthOnboardingIntro>{SKIP_WARNING}</AuthOnboardingIntro>

                        <AuthOnboardingActions>
                            <AuthMethodButton
                                $type="blue"
                                $SVGtype="openNewTab"
                                disabled={loading}
                                onClickHandler={() => visitChromeBrowserDownload()}
                            >
                                Get Google Chrome
                            </AuthMethodButton>
                            <AuthSkipLink
                                type="button"
                                disabled={loading}
                                onClick={() => void skipToSurveyClick()}
                            >
                                {`Continue in ${browserName}`}
                            </AuthSkipLink>
                        </AuthOnboardingActions>
                    </AuthOnboardingCardBody>
                </AuthOnboardingCard>
            </ConfirmStepContent>
        </OnboardingStepLayout>
    )
}

export default UnsupportedBrowserSection

const ConfirmStepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: 100%;
`
